import React, {Fragment, useEffect, useState} from "react";
import './SensorReportsChart.scss'
import {
    CartesianAxis,
    CartesianGrid,
    Legend,
    Line,
    LineChart, ReferenceArea, ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import {formatDate, formatDateWithTime} from "../../helpers/helpers";


/**
 * Translation map for parameter keys from the new API response.
 * Used for chart option labels.
 */
const parameterKeyLabels = {
    'temperature': 'Temperature',
    'humidity': 'Humidity',
    'soil_moisture': 'Soil Moisture',
    'soil_conductivity': 'Soil Conductivity',
    'ph': 'pH',
    'ec': 'EC',
    'wind_speed': 'Wind Speed',
    'wet_leaf': 'Wet Leaf',
    'rain': 'Rain',
    'battery': 'Battery',
};


const SensorReportsChart = props => {
    const {data, measurementPeriod} = props;

    const [chartData, setChartData] = useState([])
    const [chartOptions, setChartOptions] = useState([])
    const [selectedChartOption, setSelectedChartOption] = useState(null)
    const [selectedChartData, setSelectedChartData] = useState(null)
    const [innerPending, setInnerPending] = useState(false)

    useEffect(() => {
        if (data && data.columns && data.rows) {
            createData(data)
        }
    },[data])

    /**
     * Builds chart options from the new API response.
     * Groups columns by parameterKey and creates selectable chart options.
     *
     * New response format:
     *   columns: [{ key, parameterKey, statisticKey, unit, chartType }]
     *   rows: [{ timestampFrom, timestampTo, values: { [key]: BigDecimal } }]
     */
    const createData = (data) => {
        setInnerPending(true)

        // Group columns by parameterKey
        const parameterGroups = {};
        data.columns.forEach(col => {
            if (!parameterGroups[col.parameterKey]) {
                parameterGroups[col.parameterKey] = {
                    parameterKey: col.parameterKey,
                    label: parameterKeyLabels[col.parameterKey] || col.parameterKey,
                    columns: [],
                    chartType: col.chartType || 'line',
                };
            }
            parameterGroups[col.parameterKey].columns.push(col);
        });

        const options = Object.values(parameterGroups);
        setChartOptions(options)
        setChartData(data)
        setInnerPending(false)
    }


    function determineDateRewrite(date) {
        if(measurementPeriod === 'MIN_30' || measurementPeriod === 'HOUR' ||
           measurementPeriod === 'MIN_30_PERIOD' || measurementPeriod === 'HOUR_1_PERIOD') {
            return formatDateWithTime(date)
        } else {
            return formatDate(date)
        }
    }


    /**
     * Handles selection of a chart type (parameter group).
     * Transforms the new API data into the format expected by Recharts.
     *
     * Output format for each data point:
     * {
     *   date: "formatted date string",
     *   label: "Temperature",
     *   presentedFields: [
     *     { name: "Min", value: 5.2 },
     *     { name: "Avg", value: 7.1 },
     *     { name: "Max", value: 9.3 }
     *   ]
     * }
     */
    const selectChartType = (option) => {
        setSelectedChartOption(option.label)

        const columns = option.columns;
        const rows = chartData.rows;

        // Build chart data from rows
        const arr = rows.map(row => {
            const presentedFields = columns.map(col => {
                const statLabel = col.statisticKey || col.parameterKey;
                return {
                    name: statLabel,
                    value: row.values && row.values[col.key] != null ? Number(row.values[col.key]) : null
                };
            });

            return {
                date: row.timestampFrom,
                label: option.label,
                presentedFields: presentedFields
            };
        });

        // Sort by date
        const sortedArr = arr.sort((a, b) => new Date(a.date) - new Date(b.date));

        // Format dates for display
        const updatedArr = sortedArr.map(item => ({
            ...item,
            date: determineDateRewrite(item.date)
        }));

        setSelectedChartData(updatedArr)
    }

    function findMaxMinValues(data) {
        if (!Array.isArray(data) || data.length === 0) {
            return null; // Return null for empty or non-array input
        }

        let max = Number.NEGATIVE_INFINITY; // Initialize max to negative infinity
        let min = Number.POSITIVE_INFINITY; // Initialize min to positive infinity

        data.forEach((item) => {
            if (item.presentedFields) {
                item.presentedFields.forEach((field) => {
                    if (field.name === "max" && field.value > max) {
                        max = field.value;
                    }
                    if (field.name === "min" && field.value < min) {
                        min = field.value;
                    }
                });
            }
        });

        return { max, min };
    }

    function calculateMaxMinusMin(data) {
        const { max, min } = findMaxMinValues(data);

        if (typeof max !== 'number' || typeof min !== 'number' || isNaN(max) || isNaN(min)) {
            return 5;
        }

        const result = (max - min) * 0.1;
        const array = [`dataMin -${Math.round(result)}`, `dataMax +${Math.round(result)}`]
        // return result.toFixed(2);
        return array;
    }


    if(innerPending) {
        return <div>loading</div>
    }

    function getColor(name) {
        const colors = {
            'min': 'green',
            'ave': '#8884d8',
            'avg': '#8884d8',
            'max': 'red',
            'sum': '#ff7300',
            'minutes': '#82ca9d',
            'value': '#8884d8',
        };

        return colors[name] || '#8884d8'; // Default to purple if the name is not recognized
    }

    const renderZeroReferenceOverlay = (data) => {
        if (!data || !data.length) return null;
        if (data[0].label !== 'Temperature' && data[0].label !== 'Temperatura Powietrza' && data[0].label !== 'Temperatura') return null;

        const allValues = data.flatMap((entry) =>
            entry.presentedFields
                .map((field) => field.value)
                .filter((v) => typeof v === 'number' && !isNaN(v))
        );

        if (!allValues.length) return null;

        const minValue = Math.min(...allValues);
        if (minValue > 0) return null;

        return (
            <>
                <ReferenceArea
                    y1={minValue}
                    y2={0}
                    fill="lightblue"
                    fillOpacity={0.3}
                />
                <ReferenceLine
                    y={0}
                    stroke="blue"
                    strokeWidth={2}
                    ifOverflow="visible"
                />
            </>
        );
    };

    if(chartOptions.length > 0) {
        return (
            <>
                <div className="ReportsChartsContainer">
                    <div className="choice">
                        <form className="radioForm">
                            {chartOptions && chartOptions.map((option, index) => (
                                <div className={'radioInput'} key={index}>
                                    <input id={option.label}  name={'radio1'} value={selectedChartOption} type='radio' onChange={() => selectChartType(option)} style={{cursor: 'pointer'}}/>
                                    <label htmlFor={option.label}>{option.label}</label>
                                </div>

                            ))}
                        </form>

                    </div>
                    {selectedChartData && (

                        <div className="SensorReportsChart">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={selectedChartData}
                                >
                                    <CartesianGrid  strokeDasharray="2 2" />
                                    <XAxis  dataKey={'date'} />
                                    {/*<YAxis type="number" domain={['dataMin -2', 'dataMax +2']} />*/}
                                    <YAxis type="number" domain={calculateMaxMinusMin(selectedChartData)}  />
                                    <Tooltip />
                                    <Legend />
                                    {renderZeroReferenceOverlay(selectedChartData)}
                                    {selectedChartData[0].presentedFields.map((el, index) => {
                                            return   (
                                                <Line type="monotone" name={el.name} dataKey={`presentedFields[${index}].value`} stroke={getColor(el.name)} activeDot={{ r: 8 }} key={index} />
                                            )
                                        }
                                    )}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
            </>
        )
    } else {
        return null
    }

}

export default SensorReportsChart
